import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Typography, Chip, Box, Checkbox, FormControlLabel, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import { AdminListPanel } from './AdminListPanel';
import { api } from '../../api/rpc';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import type { NotificationRecord } from '../../api/types';

const columns: GridColDef[] = [
  {
    field: 'severity',
    headerName: 'Sev',
    width: 70,
    renderCell: (params) => {
      const s = String(params.value ?? 'info').toLowerCase();
      const color = s === 'error' ? 'error' : s === 'warning' ? 'warning' : s === 'success' ? 'success' : 'info';
      return <Chip label={s.charAt(0).toUpperCase() + s.slice(1)} size="small" color={color} sx={{ fontSize: 9, height: 18 }} />;
    },
  },
  { field: 'type', headerName: 'Type', width: 120 },
  { field: 'message', headerName: 'Message', flex: 1, minWidth: 300 },
  {
    field: 'date',
    headerName: 'Date',
    width: 160,
    valueFormatter: (value: number | string | undefined) => {
      if (!value) return '—';
      const d = typeof value === 'number' ? new Date(value) : new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
  },
  {
    field: 'read',
    headerName: 'Read',
    width: 60,
    renderCell: (params) => (
      <Typography variant="caption" sx={{ fontSize: 11, fontWeight: params.value ? 400 : 700 }}>
        {params.value ? '✓' : '●'}
      </Typography>
    ),
  },
];

type SeverityFilter = 'ALL' | 'error' | 'warning' | 'info';

/**
 * Notifications admin tab — NotificationViewPanel equivalent.
 * Lists system notifications with severity filter, auto-refresh, unread highlighting.
 */
export const NotificationsPanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('ALL');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selection, setSelection] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string>() });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data, isLoading, isError } = useQuery<NotificationRecord[]>({
    queryKey: ['notifications'],
    queryFn: async () => {
      const result = await api.getNotifications();
      return result.records ?? [];
    },
    staleTime: 30_000,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  const selectedIds = useMemo(() => {
    if (selection.type !== 'include') return [] as string[];
    return [...selection.ids] as string[];
  }, [selection]);

  const handleDismissSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.markNotificationRead(selectedIds);
      pushSuccess(`Dismissed ${selectedIds.length} notification(s)`);
      setSelection({ type: 'include', ids: new Set<string>() });
      refresh();
    } catch (e) {
      pushError(`Failed to dismiss: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [selectedIds, refresh]);

  const handleClearResolved = useCallback(async () => {
    try {
      await api.clearResolvedNotifications();
      pushSuccess('Resolved notifications cleared');
      refresh();
    } catch (e) {
      pushError(`Failed to clear: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [refresh]);

  // Auto-refresh every 15s
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(refresh, 15_000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refresh]);

  const notifications = useMemo(() => {
    let all = data ?? [];
    // Severity filter
    if (severityFilter !== 'ALL') {
      all = all.filter((n) => (n.severity ?? 'info').toLowerCase() === severityFilter);
    }
    // Search
    if (search) {
      const s = search.toLowerCase();
      all = all.filter(
        (n) =>
          n.message.toLowerCase().includes(s) ||
          n.type.toLowerCase().includes(s),
      );
    }
    return all;
  }, [data, search, severityFilter]);

  // Count by severity
  const counts = useMemo(() => {
    const all = data ?? [];
    return {
      total: all.length,
      error: all.filter((n) => (n.severity ?? '').toLowerCase() === 'error').length,
      warning: all.filter((n) => (n.severity ?? '').toLowerCase() === 'warning').length,
      info: all.filter((n) => !['error', 'warning'].includes((n.severity ?? 'info').toLowerCase())).length,
      unread: all.filter((n) => !n.read).length,
    };
  }, [data]);

  const statusText = `${notifications.length} notification${notifications.length !== 1 ? 's' : ''}` +
    (counts.unread > 0 ? ` (${counts.unread} unread)` : '');

  return (
    <AdminListPanel
      title="Notifications"
      search={search}
      onSearchChange={setSearch}
      onRefresh={refresh}
      statusText={statusText}
    >
      {/* Severity filter + auto-refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25, gap: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small" exclusive value={severityFilter}
          onChange={(_, val) => val && setSeverityFilter(val)}
          sx={{ height: 24 }}
        >
          <ToggleButton value="ALL" sx={{ fontSize: 10, px: 1, py: 0 }}>All ({counts.total})</ToggleButton>
          <ToggleButton value="error" sx={{ fontSize: 10, px: 1, py: 0, color: 'error.main' }}>Error ({counts.error})</ToggleButton>
          <ToggleButton value="warning" sx={{ fontSize: 10, px: 1, py: 0, color: 'warning.main' }}>Warning ({counts.warning})</ToggleButton>
          <ToggleButton value="info" sx={{ fontSize: 10, px: 1, py: 0 }}>Info ({counts.info})</ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          control={<Checkbox size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} sx={{ p: 0.25 }} />}
          label={<Typography sx={{ fontSize: 10 }}>Auto-refresh</Typography>}
          sx={{ ml: 'auto' }}
        />
        <Button size="small" variant="outlined" disabled={selectedIds.length === 0}
          onClick={handleDismissSelected}
          sx={{ fontSize: 10, textTransform: 'none', py: 0, minHeight: 22 }}>
          Dismiss ({selectedIds.length})
        </Button>
        <Button size="small" variant="outlined" color="warning"
          onClick={handleClearResolved}
          sx={{ fontSize: 10, textTransform: 'none', py: 0, minHeight: 22 }}>
          Clear Resolved
        </Button>
      </Box>

      {/* Error state */}
      {isError && (
        <Box sx={{ px: 2, py: 1, backgroundColor: 'error.dark', color: 'error.contrastText', fontSize: 12 }}>
          Failed to load notifications. Click Refresh to retry.
        </Box>
      )}

      <DataGrid
        rows={notifications}
        columns={columns}
        getRowId={(row) => (row as NotificationRecord).id}
        loading={isLoading}
        density="compact"
        disableColumnMenu
        checkboxSelection
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        rowHeight={24}
        columnHeaderHeight={28}
        sx={{
          border: 'none',
          fontSize: 12,
          '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
          '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          // Bold unread rows
          '& .MuiDataGrid-row': {
            '&.unread': { fontWeight: 700 },
          },
        }}
        getRowClassName={(params) => (params.row as NotificationRecord).read ? '' : 'unread'}
      />
    </AdminListPanel>
  );
};
